import type { IconButtonProps } from '@hover/blueprint';
import { system } from '@hover/blueprint';
import type { TFunction } from 'i18next';
import React from 'react';
import { Trans } from 'react-i18next';

import { MaterialCalloutType } from 'src/modules/homeownerApp/constants/productProviders/configuration';
import {
  getMaterialCalloutDisplay,
  getProductProviderConfiguration,
} from 'src/modules/homeownerApp/helpers/productProviders';
import {
  getFacetValue,
  isGenericProduct,
} from 'src/modules/homeownerApp/helpers/products';
import type { VariationEdit } from 'src/modules/homeownerApp/hooks/useVariationEdits';
import type { ProductCatalogVariationFragment } from 'src/shared/graphql/generated';

export type EditDescriptionLabels = {
  primaryLabel: string | React.ReactNode;
  secondaryLabel: string | React.ReactNode;
  tertiaryLabel?: string | React.ReactNode;
  tertiaryIcon?: IconButtonProps['icon'];
  altText: string;
};

export type FormatEditDescriptionParams = {
  t: TFunction;
  variation: ProductCatalogVariationFragment;
  region: string;
  kind: 'paint' | 'material';
};

const transComponents = { Bold: <system.span fontWeight="bodyBold" /> };

const PrimaryLabelIn = ({
  field1,
  field2,
  t,
}: {
  field1: string;
  field2: string;
  t: TFunction;
}) => (
  <Trans
    components={transComponents}
    i18nKey="edits.primaryLabelIn"
    t={t}
    values={{ field1, field2 }}
  />
);

const PrimaryLabelSingle = ({
  field1,
  t,
}: {
  field1: string;
  t: TFunction;
}) => (
  <Trans
    components={transComponents}
    i18nKey="edits.primaryLabelSingle"
    t={t}
    values={{ field1 }}
  />
);

const SecondaryLabelBy = ({
  field1,
  field2,
  t,
}: {
  field1: string;
  field2: string;
  t: TFunction;
}) => (
  <Trans
    components={transComponents}
    i18nKey="edits.secondaryLabelBy"
    t={t}
    values={{ field1, field2 }}
  />
);

const SecondaryLabelSingle = ({
  field1,
  t,
}: {
  field1: string;
  t: TFunction;
}) => (
  <Trans
    components={transComponents}
    i18nKey="edits.secondaryLabelSingle"
    t={t}
    values={{ field1 }}
  />
);

const formatRoofing = (
  params: FormatEditDescriptionParams,
): EditDescriptionLabels => {
  const { t, variation } = params;

  const color = variation.genAiColor?.name ?? '';
  const productLine = getFacetValue(variation, 'product_line') ?? '';
  const providerName = variation.product.provider?.name ?? '';

  return {
    primaryLabel: <PrimaryLabelSingle field1={color} t={t} />,
    secondaryLabel: (
      <SecondaryLabelBy field1={productLine} field2={providerName} t={t} />
    ),
    altText: t('edits.altTextSingle', { field1: color }),
  };
};

const formatWindows = (
  params: FormatEditDescriptionParams,
): EditDescriptionLabels => {
  const { t, variation } = params;

  const colorGroup = getFacetValue(variation, 'color_group') ?? '';
  const grillePattern = getFacetValue(variation, 'grille_pattern') ?? '';
  const windowType = getFacetValue(variation, 'window_type') ?? '';
  const providerName = variation.product.provider?.name ?? '';

  return {
    primaryLabel: (
      <PrimaryLabelIn field1={grillePattern} field2={windowType} t={t} />
    ),
    secondaryLabel: (
      <SecondaryLabelBy field1={colorGroup} field2={providerName} t={t} />
    ),
    altText: t('edits.altTextIn', {
      field1: grillePattern,
      field2: windowType,
    }),
  };
};

const formatWalls = (params: FormatEditDescriptionParams) => {
  const { variation, t } = params;

  const size = getFacetValue(variation, 'SIZING') ?? '';
  const productLine = getFacetValue(variation, 'product_line') ?? '';
  const color = variation.genAiColor?.name || '';
  const providerName = variation.product.provider?.name ?? '';

  return {
    primaryLabel: <PrimaryLabelIn field1={color} field2={size} t={t} />,
    secondaryLabel: (
      <SecondaryLabelBy field1={productLine} field2={providerName} t={t} />
    ),
    altText: t('edits.altTextIn', { field1: color, field2: size }),
  };
};

const formatTrim = (params: FormatEditDescriptionParams) => {
  const { t, variation } = params;

  const productLine = getFacetValue(variation, 'product_line') ?? '';
  const color = variation.genAiColor?.name || '';
  const providerName = variation.product.provider?.name ?? '';

  return {
    primaryLabel: <PrimaryLabelSingle field1={color} t={t} />,
    secondaryLabel: (
      <SecondaryLabelBy field1={productLine} field2={providerName} t={t} />
    ),
    altText: t('edits.altTextSingle', { field1: color }),
  };
};

const formatPaint = (params: FormatEditDescriptionParams) => {
  const { region, t, variation } = params;

  const { name } = variation;
  const providerName = variation.product.provider?.name ?? '';

  return {
    primaryLabel: <PrimaryLabelIn field1={region} field2={name} t={t} />,
    secondaryLabel: <SecondaryLabelSingle field1={providerName} t={t} />,
    altText: t('edits.altTextIn', { field1: region, field2: name }),
  };
};

const formatGeneric = (params: FormatEditDescriptionParams) => {
  const { region, t, variation } = params;

  const name = variation.name ?? '';

  return {
    primaryLabel: <PrimaryLabelSingle field1={region} t={t} />,
    secondaryLabel: <SecondaryLabelSingle field1={name} t={t} />,
    altText: t('edits.altTextIn', { field1: region, field2: name }),
  };
};

export const setProviderTertiaryData = (
  params: FormatEditDescriptionParams,
  description: EditDescriptionLabels,
): EditDescriptionLabels => {
  const { t, variation } = params;
  const providerUuid = variation.product.provider?.id;
  const providerName = variation.product.provider?.name;

  if (!providerUuid) {
    return description;
  }

  const providerConfig = getProductProviderConfiguration(providerUuid);

  if (providerConfig.calloutType === MaterialCalloutType.NONE) {
    return description;
  }

  const materialCalloutDisplay = getMaterialCalloutDisplay(
    providerConfig.calloutType,
  );

  return {
    tertiaryLabel: t(materialCalloutDisplay.i18nKey, {
      provider: providerName,
    }),
    tertiaryIcon: materialCalloutDisplay.icon,
    ...description,
  };
};

export const formatEditDescriptions = (
  params: FormatEditDescriptionParams,
): EditDescriptionLabels => {
  const isGeneric = isGenericProduct(params.variation);
  const isPaint = params.kind === 'paint';

  if (isPaint) {
    const paintDescription = formatPaint(params);
    return setProviderTertiaryData(params, paintDescription);
  }

  if (params.region === 'Roofing' && !isGeneric) {
    const roofDescription = formatRoofing(params);
    return setProviderTertiaryData(params, roofDescription);
  }

  if (params.region === 'Trim' && !isGeneric) {
    const trimDescription = formatTrim(params);
    return setProviderTertiaryData(params, trimDescription);
  }

  if (params.region === 'Windows' && !isGeneric) {
    const windowDescription = formatWindows(params);
    return setProviderTertiaryData(params, windowDescription);
  }

  if (params.region === 'Walls' && !isGeneric) {
    const wallDescription = formatWalls(params);
    return setProviderTertiaryData(params, wallDescription);
  }

  return formatGeneric(params);
};

export const formatVariationEditDescriptions = (
  t: TFunction,
  edit: VariationEdit,
): EditDescriptionLabels | null => {
  if (!edit.variation) return null;

  return formatEditDescriptions({
    t,
    variation: edit.variation,
    kind: edit.kind,
    region: edit.region,
  });
};
